import React from 'react';
import { Loader2 } from 'lucide-react';
import { ThemeProvider } from '@/contexts/ThemeContext';

const DUCK_FACTS = [
  "Allocating ducks to tenancy zones...",
  "Quack! Tailoring your workspace environment.",
  "Synchronizing tenancy structures...",
  "Just a moment while we prepare your flock's configuration.",
  "Finalizing secure tenancy scaffolding..."
];

export const DuckLoadingScreen: React.FC<{ 
  title?: string; 
  text?: string; 
  duckFacts?: string[]; 
  fullScreen?: boolean;
  /** 'overlay' for onboarding modals (more opaque), 'default' for subtle backgrounds */
  variant?: 'default' | 'overlay';
}> = ({ 
  title = 'Loading your tenancy...', 
  text = 'Just a moment while we prepare your environment', 
  duckFacts = DUCK_FACTS, 
  fullScreen = false,
  variant = 'default'
}) => {
  // Animation for the duck image
  const duckAnimation = {
    animation: 'bounce 1.5s ease-in-out infinite',
    transformOrigin: 'center bottom'
  };

  // Background classes based on variant
  const bgClass = variant === 'overlay' 
    ? 'from-card/90 to-card/90 dark:from-card/90 dark:to-card/90' 
    : 'from-accent/5 to-accent/10 dark:from-accent/5 dark:to-accent/10';

  return (
    <ThemeProvider>
      <div className={`flex flex-col items-center justify-center bg-linear-to-b ${bgClass} backdrop-blur-xl ${fullScreen ? 'fixed inset-0 z-50' : 'absolute inset-0'}`}>
        <div className="relative w-64 h-64 mb-8">
          {/* Duck image */}
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src="/assets/img/404.png"
              alt="Duck"
              className="w-48 h-48 object-contain"
              style={duckAnimation}
            />
          </div>

          {/* Water ripples */}
          <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="absolute w-10 h-2 bg-accent/50 dark:bg-accent/50 rounded-full"
                style={{
                  bottom: `${i * 4}px`,
                  left: '50%',
                  transform: `translateX(-50%) scale(${1 - (i * 0.1)})`,
                  opacity: 1 - (i * 0.3),
                  animation: `ripple 1.5s ease-in-out ${i * 0.1}s infinite`
                }}
              />
            ))}
          </div>

          {/* Animation styles */}
          <style dangerouslySetInnerHTML={{
            __html: `
            @keyframes bounce {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-10px); }
            }
            @keyframes ripple {
              0% { transform: translateX(-50%) scale(1); opacity: 1; }
              100% { transform: translateX(-50%) scale(1.5); opacity: 0; }
            }
          `
          }} />
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{title}</h2>
          <p className="text-gray-600 dark:text-gray-300">{text}</p>
          <div className="mt-4 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-accent" />
          </div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {duckFacts[Math.floor(Math.random() * duckFacts.length)]}
          </p>
        </div>
      </div>
    </ThemeProvider>
  );
};
